import React from 'react';
import { gql } from '@apollo/client';
import { Alert, Button, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import CopyToClipboardButton from 'components/CopyToClipboardButton';
import { useMutation, useQuery } from 'hooks/apollo';
import useFormatMessage from 'hooks/useFormatMessage';
import useGlobalContext from 'hooks/useGlobalContext';
import globalMessages from 'translations/messages';
import { KB_EXPORT_API } from 'shared/constants/urls';
import useCustomer from 'hooks/useCustomer';
import { EDIT_API_KEYS } from 'shared/constants/permissions';
import Loading from 'components/Loading';
import messages from './messages';

const QUERY = gql`
  query EditCustomer_GetApiKeys($id: ID!) {
    customer(id: $id) {
      id
      apiKeys {
        id
        createdAt
        updatedAt
        lastUsedAt
      }
    }
  }
`;

const CREATE_API_KEY = gql`
  mutation CreateApiKey($customerId: ID!) {
    createApiKey(customerId: $customerId) {
      id
      createdAt
      updatedAt
    }
  }
`;

const DELETE_API_KEY = gql`
  mutation DeleteApiKey($id: ID!) {
    deleteApiKey(id: $id) {
      id
    }
  }
`;

const CustomerApiKeys = ({ customer }) => {
  const $t = useFormatMessage();
  const { hasCustomerPermission, loading: customerLoading } = useCustomer();
  const { modal, message } = useGlobalContext();
  const canEdit = hasCustomerPermission(EDIT_API_KEYS);
  const { data, loading, refetch } = useQuery(QUERY, {
    variables: {
      id: customer.id,
    },
  }, {
    skip: !canEdit,
  });
  const [createApiKey, { loading: creating }] = useMutation(CREATE_API_KEY);
  const [deleteApiKey, { loading: deleting }] = useMutation(DELETE_API_KEY);

  const columns = [
    {
      dataIndex: 'id',
      title: $t(messages.apiKey),
      render: (value) => (
        <Input.Password
          value={value}
          readOnly
          visibilityToggle
          addonAfter={(
            <CopyToClipboardButton textToCopy={value} type="text" size="small" hideTooltip />
          )}
        />
      ),
    },
    {
      dataIndex: 'createdAt',
      title: $t(globalMessages.created),
      type: 'datetime',
      defaultSortOrder: 'descend',
    },
    {
      dataIndex: 'updatedAt',
      title: $t(globalMessages.updated),
      type: 'datetime',
      visible: false,
    },
    {
      dataIndex: 'lastUsedAt',
      title: 'Last used',
      type: 'datetime',
    },
  ];

  const onCreate = async () => {
    await createApiKey({
      variables: {
        customerId: customer.id,
      },
    });
    message.success($t(messages.apiKeyCreated));
    refetch();
  };

  const onDelete = (apiKey) => {
    modal.confirm({
      title: $t(globalMessages.confirmDelete, { title: apiKey.id }),
      okText: $t(globalMessages.delete),
      okType: 'danger',
      async onOk() {
        await deleteApiKey({ variables: { id: apiKey.id } });
        refetch();
      },
    });
  };

  const actionsMenuItems = (apiKey) => ([{
    key: 'delete',
    label: $t(globalMessages.delete),
    danger: true,
    onClick: () => onDelete(apiKey),
  }]);

  if (customerLoading) {
    return <Loading />;
  }

  if (!canEdit) {
    return <Alert message="You don't have permission to access API keys" type="error" showIcon />;
  }

  return (
    <>
      <Alert
        message={(
          <>
            API keys are used to authenticate requests to the Extellio API. You can find documentation
            {' '}
            <a href={KB_EXPORT_API} target="_blank" rel="noopener noreferrer">here</a>
            .
          </>
        )}
        type="info"
        showIcon
        style={{ marginBottom: '1em' }}
      />
      <Table
        loading={loading || creating || deleting}
        columns={columns}
        dataSource={data?.customer?.apiKeys}
        actionsMenuItems={actionsMenuItems}
        footerActions={(
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreate}>
            {$t(messages.addApiKey)}
          </Button>
        )}
      />
    </>
  );
};

export default CustomerApiKeys;
